import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import {
  contextCommentApi,
  type ContextCommentKind,
  type ContextCommentOwnerScope,
  type ContextCommentListResponse,
  type ContextCommentSearchResponse,
  type CreateContextCommentPayload,
  type CreateContextCommentResponse,
} from "lib/api/contextComments";

/**
 * Query keys for context-comment attachments. Tuple shape keeps
 * invalidation surgical (e.g. invalidating only the principles on a
 * given post does not invalidate tasks or other posts).
 */
export const contextCommentQueryKeys = {
  list: (postId: number, kind: ContextCommentKind) =>
    ["contextComments", "list", postId, kind] as const,
  search: (
    kind: ContextCommentKind,
    query: string,
    ownerScope: ContextCommentOwnerScope,
    audiencesKey: string
  ) =>
    [
      "contextComments",
      "search",
      kind,
      query,
      ownerScope,
      audiencesKey,
    ] as const,
};

/** Limit used for each page of global search results. Must match the
 * `limit` passed to `contextCommentApi.search` for `hasNextPage` to be
 * computed correctly from the returned global-row count. */
const PICKER_PAGE_SIZE = 10;

/**
 * Fetch the viewer-visible principle or task comments attached to a
 * host post. Disabled until a valid positive post id is provided.
 */
export const useContextCommentsListQuery = (
  postId: number | null | undefined,
  kind: ContextCommentKind,
  options?: { enabled?: boolean }
) =>
  useQuery<ContextCommentListResponse>({
    queryKey:
      typeof postId === "number" && postId > 0
        ? contextCommentQueryKeys.list(postId, kind)
        : ["contextComments", "list", "empty", kind],
    queryFn: () => contextCommentApi.listForPost(postId as number, kind),
    enabled:
      typeof postId === "number" &&
      postId > 0 &&
      (options?.enabled === undefined ? true : options.enabled),
  });

/**
 * Single-page search helper kept for legacy consumers (unused by the
 * picker after the infinite-scroll refactor, but retained so any
 * external callers keep compiling).
 */
export const useContextCommentsSearchQuery = (
  kind: ContextCommentKind,
  query: string,
  ownerScope: ContextCommentOwnerScope,
  enabled: boolean,
  audiences?: string[]
) => {
  const audiencesKey = (audiences ?? []).slice().sort().join("|");
  return useQuery<ContextCommentSearchResponse>({
    queryKey: contextCommentQueryKeys.search(kind, query, ownerScope, audiencesKey),
    queryFn: () =>
      contextCommentApi.search({
        kind,
        q: query.trim().length >= 1 ? query : undefined,
        ownerScope,
        audiences,
      }),
    enabled,
    staleTime: 10 * 1000,
  });
};

/**
 * Infinite search for the ContextCommentPicker. Page 0 returns the
 * caller's own rows (when `ownerScope` permits) plus the first page of
 * global rows. Subsequent pages return only additional global rows,
 * paginated by offset. Consumers flatten `pages[n].data.global` for the
 * scroll list while reading `pages[0].data.own` once at the top.
 */
export const useContextCommentsSearchInfiniteQuery = (
  kind: ContextCommentKind,
  query: string,
  ownerScope: ContextCommentOwnerScope,
  audiences: string[] | undefined,
  enabled: boolean
) => {
  const audiencesKey = (audiences ?? []).slice().sort().join("|");
  return useInfiniteQuery<ContextCommentSearchResponse>({
    queryKey: contextCommentQueryKeys.search(kind, query, ownerScope, audiencesKey),
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      contextCommentApi.search({
        kind,
        q: query.trim().length >= 1 ? query : undefined,
        ownerScope,
        audiences,
        limit: PICKER_PAGE_SIZE,
        offset: typeof pageParam === "number" ? pageParam : 0,
      }),
    getNextPageParam: (lastPage, allPages) => {
      const lastGlobalCount = lastPage.data.global.length;
      if (lastGlobalCount < PICKER_PAGE_SIZE) {
        return undefined;
      }
      return allPages.reduce(
        (total, page) => total + page.data.global.length,
        0
      );
    },
    enabled,
    staleTime: 10 * 1000,
  });
};

/**
 * Mutation helpers.
 */
export const useCreateOrLinkContextCommentMutation = (
  postId: number,
  kind: ContextCommentKind
) => {
  const queryClient = useQueryClient();
  return useMutation<CreateContextCommentResponse, unknown, CreateContextCommentPayload>({
    mutationFn: (payload) => contextCommentApi.createOrLink(postId, kind, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: contextCommentQueryKeys.list(postId, kind),
      });
      await queryClient.invalidateQueries({ queryKey: ["contextComments", "search", kind] });
    },
  });
};

export const useUnlinkContextCommentMutation = (
  postId: number,
  kind: ContextCommentKind
) => {
  const queryClient = useQueryClient();
  return useMutation<{ detached: boolean }, unknown, { commentId: number }>({
    mutationFn: ({ commentId }) => contextCommentApi.unlink(postId, kind, commentId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: contextCommentQueryKeys.list(postId, kind),
      });
    },
  });
};

export const useReorderContextCommentsMutation = (
  postId: number,
  kind: ContextCommentKind
) => {
  const queryClient = useQueryClient();
  return useMutation<{ count: number }, unknown, { order: number[] }>({
    mutationFn: ({ order }) => contextCommentApi.reorder(postId, kind, order),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: contextCommentQueryKeys.list(postId, kind),
      });
    },
  });
};
